// Command palette (⌘K / Ctrl+K, the rail's search): first what the open screen finds (R-U23, «На этом экране»), then any record,
// section, view or screen of the system, the items and settings of «Настройки», recent records, create a record, switch theme.

import { useMemo, useState } from 'react';
import { canRead, canWrite, recordTitle, textKey } from '@confdeck/core-shared';
import { PickList, type PickOption } from '../ui/PickList.tsx';
import { createRecord } from '../sync/mutations.ts';
import { useAccess, useMeta, useSync } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { Dialog } from '../ui/primitives.tsx';
import { navigate, recordHref, typeHref } from './router.ts';
import { setVariant } from './variants.tsx';
import { layoutIcon } from '../table/ViewMenu.tsx';
import { recentRecords } from './recent.ts';
import { offeredViews } from '../table/view-access.ts';
import { HERE_GROUP, hereFirst, searchHere } from './screen-search.ts';
import { settingsHitId, useSettingsSearch } from '../settings/SettingsArea.tsx';
import s from './shell.module.css';

const PER_TYPE = 6;

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} label="Поиск и команды">
      {open && <PaletteBody onClose={onClose} />}
    </Dialog>
  );
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const client = useSync();
  const meta = useMeta();
  const access = useAccess();
  const [actions] = useState(() => new Map<string, () => void>());
  const settings = useSettingsSearch();
  const base = useMemo(() => {
    const out: (PickOption & { run: () => void })[] = [];
    // Only the sections the person reads, and «Создать» only where they write (F4 audit №7): a volunteer sees their own shell.
    for (const t of meta.types()) {
      if (!canRead(access, t.id)) continue;
      out.push({ id: `type:${t.id}`, label: t.nameMany, group: 'Разделы', render: <span className={s.palItem}><Icon name={t.icon} size={15} />{t.nameMany}</span>, run: () => navigate(typeHref(t.key)) });
      for (const v of offeredViews(meta, access, client.header?.userId, t.id)) out.push({ id: `view:${v.id}`, label: `${t.nameMany} · ${v.name}`, group: 'Виды', render: <span className={s.palItem}><Icon name={layoutIcon(v.layout)} size={15} />{t.nameMany} · {v.name}</span>, run: () => navigate(typeHref(t.key, v.id)) });
      if (canWrite(access, t.id)) out.push({ id: `new:${t.id}`, label: `Создать: ${t.name.toLowerCase()}`, group: 'Действия', render: <span className={s.palItem}><Icon name="plus" size={15} />Создать: {t.name.toLowerCase()}</span>, run: () => { const id = createRecord(client, t.id, { [t.titleField]: 'Без названия', ...requiredDefaults(t.id) }); navigate(recordHref(id)); } });
    }
    // Screens of the person's own menu (the schedule, the PC queue, settings): the same list the rail shows (audit 28.10, L4).
    for (const m of client.menu) for (const sc of m.screens ?? []) out.push({ id: `screen:${sc.key}`, label: sc.label, group: 'Экраны', render: <span className={s.palItem}><Icon name={sc.icon ?? m.icon} size={15} />{sc.label}</span>, run: () => navigate(`/s/${sc.key}`) });
    out.push({ id: 'theme:dark', label: 'Тёмная тема', group: 'Действия', render: <span className={s.palItem}><Icon name="moon" size={15} />Тёмная тема</span>, run: () => setVariant('theme', 'dark') });
    out.push({ id: 'theme:light', label: 'Светлая тема', group: 'Действия', render: <span className={s.palItem}><Icon name="sun" size={15} />Светлая тема</span>, run: () => setVariant('theme', 'light') });
    // One group per kind: sections, then views, then actions (audit #19).
    const rank: Record<string, number> = { 'Разделы': 0, 'Экраны': 1, 'Виды': 2, 'Действия': 3 };
    return out.sort((a, b) => (rank[a.group ?? ''] ?? 9) - (rank[b.group ?? ''] ?? 9));

    function requiredDefaults(typeId: string) {
      const d: Record<string, unknown> = {};
      for (const f of meta.fields(typeId)) if (f.required && f.fieldType === 'taxonomy' && f.options.taxonomyId) {
        const tax = meta.taxonomy(f.options.taxonomyId)!;
        const first = tax.style === 'pipeline' ? meta.children(meta.children(null, tax.id)[0]?.id ?? null, tax.id)[0] : meta.terms(tax.id)[0];
        if (first) d[f.key] = f.options.multiple ? [first.id] : first.id;
      }
      return d;
    }
  }, [meta, meta.all.size, client, access]);

  // No title index: measured on 150k records (stage E), a plain pass that stops at the first matches of each section answers
  // a key in 14–27 ms (median); a cached index was not faster and its first fill raised p90 to 90–180 ms.
  const options = (q: string): PickOption[] => {
    const k = textKey(q);
    actions.clear();
    type Opt = PickOption & { run: () => void; recordId?: string };
    // The open screen first: its letters, articles, pages, forms, rows — by its own rules (um/2026-10-31/02).
    const hits = searchHere(q);
    const here: Opt[] = hits.map((h) => ({ id: `here:${h.id}`, label: h.label, group: HERE_GROUP, recordId: h.recordId, run: h.run,
      render: <span className={s.palItem}><Icon name={h.icon ?? 'search'} size={15} />{h.label}{h.hint && <span className={s.palHint}>{h.hint}</span>}</span> }));
    const list: Opt[] = [];
    if (k) {
      // Records first: the palette is mostly «find that company».
      for (const t of meta.types()) {
        let n = 0;
        for (const id of client.store.recordIds(t.id)) {
          const r = client.store.record(id);
          if (!r) continue;
          const title = recordTitle(r, meta);
          if (!textKey(title).includes(k)) continue;
          list.push({ id: `rec:${id}`, recordId: id, label: title, group: t.nameMany, hint: '', render: <span className={s.palItem}><Icon name={t.icon} size={15} />{title}</span>, run: () => navigate(recordHref(id)) });
          if (++n >= PER_TYPE) break;
        }
      }
      // Items and settings of «Настройки» (decision 038 п.3); on a page of «Настройки» they are already above, «На этом экране».
      const shown = new Set(hits.map((h) => h.id));
      for (const h of settings(q).filter((x) => !shown.has(settingsHitId(x))).slice(0, PER_TYPE)) {
        list.push({ id: settingsHitId(h), label: h.label, group: 'Настройки', run: h.run,
          render: <span className={s.palItem}><Icon name={h.key ? 'gear' : 'settings'} size={15} />{h.label}<span className={s.palHint}>{h.key ? h.itemLabel : 'Настройки'}</span></span> });
      }
    }
    // Without a query the recent records come first: «вернуться к тому, что открывал».
    if (!k) for (const id of recentRecords()) {
      const r = client.store.record(id);
      const t = r && meta.type(r.typeId);
      if (!r || r.archivedAt || !t || !canRead(access, t.id)) continue;
      const title = recordTitle(r, meta);
      list.push({ id: `recent:${id}`, label: title, group: 'Недавние', render: <span className={s.palItem}><Icon name={t.icon} size={15} />{title}<span className={s.palHint}>{t.name}</span></span>, run: () => navigate(recordHref(id)) });
    }
    for (const o of base) if (!k || textKey(o.label).includes(k)) list.push(o);
    const all = hereFirst(here, list, new Set(hits.map((h) => h.recordId).filter((x): x is string => !!x)), (o) => o.recordId);
    for (const o of all) actions.set(o.id, o.run);
    return all.slice(0, 60);
  };

  return (
    <div className={s.palette}>
      <PickList options={options} selected={[]} placeholder="Найти запись, раздел или действие…" emptyText="Ничего не нашлось"
        onPick={(id) => { actions.get(id)?.(); onClose(); }} />
    </div>
  );
}

